/**
 * DraggableDialog Component
 *
 * Wrapper que convierte cualquier Dialog de shadcn/ui en draggable.
 * Mantiene toda la funcionalidad del Dialog original pero permite al usuario
 * arrastrar el diálogo por la pantalla.
 *
 * @example
 * ```tsx
 * <DraggableDialog open={open} onOpenChange={setOpen}>
 *   <DraggableDialogContent>
 *     <DialogHeader>
 *       <DialogTitle>Mi Dialog Draggable</DialogTitle>
 *     </DialogHeader>
 *     <div>Contenido...</div>
 *     <DialogFooter>
 *       <Button>Cerrar</Button>
 *     </DialogFooter>
 *   </DraggableDialogContent>
 * </DraggableDialog>
 * ```
 */

'use client';

import * as React from 'react';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { GripVertical, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import { VisuallyHidden } from './visually-hidden';

// Re-exportar componentes base del Dialog
export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
export const DialogPortal = DialogPrimitive.Portal;

// Overlay con z-index de variables CSS + Fluent-style backdrop
export const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 bg-black/40 backdrop-blur-[4px]',
      'data-[state=open]:animate-in data-[state=closed]:animate-out',
      'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      'z-[var(--z-modal-backdrop)]', // Usa variable CSS
      className
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

// DialogContent Draggable
export interface DraggableDialogContentProps
  extends React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> {
  /** Si el dialog es draggable (default: true) */
  draggable?: boolean;
  /** Callback cuando se suelta el drag */
  onDragStop?: (e: DraggableEvent, data: DraggableData) => void;
  /** Mostrar el handle de arrastre (default: true) */
  showHandle?: boolean;
  /** Ocultar el botón de cerrar (cuando el contenido tiene su propio botón) */
  hideCloseButton?: boolean;
}

const HANDLE_SELECTOR = '.drag-handle, [data-dialog-drag-handle="true"]';
const CANCEL_SELECTOR = '.no-drag, input, textarea, button:not(.drag-handle), select, [contenteditable], [data-dialog-drag-cancel="true"]';

export const DraggableDialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DraggableDialogContentProps
>(({ className, children, draggable = true, onDragStop, showHandle = false, hideCloseButton = false, ...props }, ref) => {
  const nodeRef = React.useRef<HTMLDivElement>(null);
  const [position, setPosition] = React.useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = React.useState(false);
  const [draggableReady, setDraggableReady] = React.useState(false);

  // Combinar refs para que tanto el componente padre como react-draggable puedan acceder
  const combinedRef = React.useCallback((node: HTMLDivElement | null) => {
    nodeRef.current = node;

    if (typeof ref === 'function') {
      ref(node);
    } else if (ref) {
      (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
    }
  }, [ref]);

  React.useLayoutEffect(() => {
    const ready = Boolean(nodeRef.current && draggable);
    if (ready !== draggableReady) {
      setDraggableReady(ready);
    }
  }, [draggable, draggableReady]);

  // Reset position al abrir
  React.useEffect(() => {
    setPosition({ x: 0, y: 0 });
  }, [props.onPointerDownOutside]); // Se resetea cuando cambia el estado

  const handleDragStart = () => {
    setIsDragging(true);
  };

  const handleDragStop = (e: DraggableEvent, data: DraggableData) => {
    setIsDragging(false);
    setPosition({ x: data.x, y: data.y });
    onDragStop?.(e, data);
  };

  // Check if children contains a DialogTitle
  const hasTitle = React.Children.toArray(children).some(
    (child) => React.isValidElement(child) && (
      child.type === DialogTitle ||
      ((child.props as any)?.children && React.Children.toArray((child.props as any).children).some(
        (nested) => React.isValidElement(nested) && nested.type === DialogTitle
      ))
    )
  );

  const content = (
    <DialogPrimitive.Content
      ref={draggable ? combinedRef : ref}
      className={cn(
        'fixed left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%]',
        'flex w-full max-w-2xl max-h-[85vh] flex-col overflow-hidden border bg-background nowheel',
        'sm:rounded-lg shadow-fluent-16 dark:shadow-fluent-64',
        'data-[state=open]:animate-in data-[state=closed]:animate-out',
        'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
        'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
        'data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]',
        'data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]',
        'data-[state=open]:duration-150 data-[state=closed]:duration-100',
        'z-[var(--z-modal)]', // Normal state
        isDragging && 'z-[var(--z-dialog-dragging)]', // Dragging state - al frente
        className
      )}
      onWheelCapture={(e) => e.stopPropagation()}
      aria-describedby={props['aria-describedby'] || undefined}
      {...props}
    >
      {/* Handle de arrastre */}
      {draggable && showHandle && (
        <div
          className="drag-handle absolute top-2 left-1/2 -translate-x-1/2 cursor-move z-10"
          role="button"
          tabIndex={0}
          aria-label="Arrastrar diálogo"
          onKeyDown={(e) => {
            // TODO: Implementar movimiento por teclado
            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
              e.preventDefault();
              // Aquí se podría implementar movimiento por teclado
            }
          }}
        >
          <GripVertical className="h-4 w-4 text-muted-foreground hover:text-foreground transition-colors" />
        </div>
      )}

      {/* Add hidden title if not present for accessibility */}
      {!hasTitle && (
        <VisuallyHidden>
          <DialogTitle>Diálogo</DialogTitle>
        </VisuallyHidden>
      )}

      {children}

      {/* Botón de cerrar - oculto si hideCloseButton=true */}
      {!hideCloseButton && (
        <DialogPrimitive.Close className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground no-drag">
          <X className="h-4 w-4" />
          <span className="sr-only">Cerrar</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  );

  if (!draggable) {
    return <DialogPortal><DialogOverlay />{content}</DialogPortal>;
  }

  return (
    <DialogPortal>
      <DialogOverlay />
      {draggableReady ? (
        <Draggable
          nodeRef={nodeRef}
          handle={HANDLE_SELECTOR}
          cancel={CANCEL_SELECTOR}
          bounds="body"
          position={position}
          onStart={handleDragStart}
          onStop={handleDragStop}
          enableUserSelectHack={false}
        >
          {content}
        </Draggable>
      ) : (
        content
      )}
    </DialogPortal>
  );
});
DraggableDialogContent.displayName = 'DraggableDialogContent';

// Re-exportar componentes restantes with Fluent accent bar
export const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'relative flex flex-col space-y-1 text-center sm:text-left',
      'before:absolute before:left-0 before:top-1 before:bottom-1 before:w-[3px] before:rounded-r-sm before:bg-primary',
      className
    )}
    data-dialog-drag-handle="true"
    {...props}
  />
);
DialogHeader.displayName = 'DialogHeader';

export const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'flex flex-col-reverse sm:flex-row sm:justify-end sm:space-x-2',
      className
    )}
    {...props}
  />
);
DialogFooter.displayName = 'DialogFooter';

export const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'text-lg font-semibold leading-snug tracking-[-0.01em]',
      className
    )}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

export const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, children, ...props }, ref) => (
  <DialogPrimitive.Description asChild ref={ref} {...props}>
    <div className={cn('text-sm text-muted-foreground', className)}>{children}</div>
  </DialogPrimitive.Description>
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

// Alias para compatibilidad
export const DraggableDialog = Dialog;
export const DraggableDialogDescription = DialogDescription;
export const DraggableDialogHeader = DialogHeader;
export const DraggableDialogFooter = DialogFooter;
export const DraggableDialogTitle = DialogTitle;
export const DraggableDialogTrigger = DialogTrigger;
export const DraggableDialogClose = DialogClose;
